import noop from 'lodash/noop';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createHook, createActionsHook } from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import type { State, Actions, CardKey } from './types.tsx';

const initialState: State = {
	cardKey: null,
	onStopCoordination: null,
};

const actions: Actions = {
	onSetActiveCard:
		(cardKey, stop) =>
		({ getState, setState }) => {
			const { onStopCoordination } = getState();
			setState({
				cardKey,
				onStopCoordination: stop ?? onStopCoordination,
			});
		},

	onEndTour:
		() =>
		({ getState, setState }) => {
			const { onStopCoordination } = getState();
			onStopCoordination && onStopCoordination();
			setState({
				cardKey: null,
				onStopCoordination: null,
			});
		},
};

const Store = createStore({ initialState, actions });

/**
 * Return `true` if the provided `cardKey` should be displayed as an active spotlight card.
 */
const useIsActiveSpotlightCardOld = createHook<State, Actions, boolean, CardKey | CardKey[]>(
	Store,
	{
		selector: (state, cardKeys) =>
			Array.isArray(cardKeys)
				? state.cardKey !== null && cardKeys.includes(state.cardKey)
				: state.cardKey === cardKeys,
	},
);

export const useIsActiveSpotlightCard: typeof useIsActiveSpotlightCardOld = (...args) => {
	const oldResult = useIsActiveSpotlightCardOld(...args);
	if (fg('remove_nin_changeboarding_rll_usages')) {
		return [
			false,
			{
				onSetActiveCard: noop,
				onEndTour: noop,
			},
		];
	}
	return oldResult;
};

const useSpotlightCoordinationActionsOld = createActionsHook<State, Actions>(Store);

export const useSpotlightCoordinationActions: typeof useSpotlightCoordinationActionsOld = (
	...args
) => {
	const oldResult = useSpotlightCoordinationActionsOld(...args);
	if (fg('remove_nin_changeboarding_rll_usages')) {
		return {
			onSetActiveCard: noop,
			onEndTour: noop,
		};
	}
	return oldResult;
};

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, jira/export/restrict-types-re-export
export type { Actions as SpotlightCoordinationActions, CardKey as SpotlightCardKey } from './types';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { cardKeys as spotlightCardKeys } from './constants';
